import { questionsApi } from './../services/questions';
import persistStore from 'redux-persist/es/persistStore';
import { agentsApi } from './../services/agents';
import {
  configureStore,
  combineReducers,
  ThunkAction,
  Action,
} from '@reduxjs/toolkit';

import { reportsApi } from '../services/reports';
import storage from 'redux-persist/lib/storage';
import { persistReducer } from 'redux-persist';
import { userApi } from '../services/user';
import homeReducer from './homeSlice';
import userReducer from './userSlice';
import clientReducer from './clientSlice';
import orderReducer from './ordersSlice';
import returnOrderReducer from './returnOrdersSlice';
import { productsApi } from '../services/products';
import { palletesApi } from '../services/palletes';
import { itemsApi } from '../services/items';
import { categoriesApi } from '../services/categories';
import { attributesApi } from '../services/attributes';
import { linesApi } from '../services/lines';
import { driversApi } from '../services/drivers';
import { rtkQueryErrorLogger } from '../middlewares/apiError';
import { vehiclesApi } from '../services/vehicles';
import { clientsApi } from '../services/clients';
import { customersApi } from '../services/customers';
import { ordersApi } from '../services/orders';
import { tifulTasksApi } from '../services/tifulTasks';
import { notificationMiddleware } from '../middlewares/notifications/middleware';
import { notificationsApi } from '../services/notification';
import { notificationPageApi } from '../Components/Pages/Main/Notifications/infrastructure/notification.api';
import notificationPageReducer from '../Components/Pages/Main/Notifications/infrastructure/notificationSlice';
import { aiConversationsApi } from '../Components/Pages/Main/Ai/infrastructure/ai-conversations.api';
import { dashboardApi } from '../services/dashboard';
import { areasApi } from '../services/areas';
import { specialOfferApi } from '../services/specialOffer';
import { citiesApi } from '../services/cities';
import { statesApi } from '../services/states';
import { inventoryMovementsApi } from '../services/inventoryMovements';
import { settingsApi } from '../services/settings';
import { stockItemsApi } from '../services/stock-items';
import { stockInventoryApi } from '../services/stock-inventory';
import { supplierApi } from '../services/suppliers';
import { warehousesApi } from '../services/warehouses';
import { stockOwnersApi } from '../services/stockOwners';
import { tableSettingsApi } from '../services/table-settings';
import { filterSettingsApi } from '../services/filter-settings';
import { searchApi } from '../services/search';
import { authApi } from '../services/auth';
import { adminsApi } from '../services/admins';
import { publicApi } from '../services/public';
import { shippingApi } from '../services/shipping';
import { attachmentsApi } from '../services/attachments';
import { invitationsApi } from '../services/invitations';
import { companyIntegrationsApi } from '../services/companyIntegrations';
import { companiesApi } from '../services/companies';
import { receiptsApi } from '../services/receipts';
import messageReducer from './messageSlice';
import customerWidgetReducer from './newCustomerWidget';
import customerItemReducer from './customerItem';
import settingsReducer from './settingsSlice';
import orderFlowReducer from './orderFlowSlice';
import authorizationReducer from './authorizationSlice';

const rootReducer = combineReducers({
  authorization: authorizationReducer,
  home: homeReducer,
  user: userReducer,
  newCustomerWidget: customerWidgetReducer,
  customerItem: customerItemReducer,
  message: messageReducer,
  client: clientReducer,
  order: orderReducer,
  returnOrder: returnOrderReducer,
  settings: settingsReducer,
  orderFlow: orderFlowReducer,
  [reportsApi.reducerPath]: reportsApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [agentsApi.reducerPath]: agentsApi.reducer,
  [productsApi.reducerPath]: productsApi.reducer,
  [palletesApi.reducerPath]: palletesApi.reducer,
  [itemsApi.reducerPath]: itemsApi.reducer,
  [categoriesApi.reducerPath]: categoriesApi.reducer,
  [attributesApi.reducerPath]: attributesApi.reducer,
  [linesApi.reducerPath]: linesApi.reducer,
  [driversApi.reducerPath]: driversApi.reducer,
  [vehiclesApi.reducerPath]: vehiclesApi.reducer,
  [clientsApi.reducerPath]: clientsApi.reducer,
  [customersApi.reducerPath]: customersApi.reducer,
  [ordersApi.reducerPath]: ordersApi.reducer,
  [tifulTasksApi.reducerPath]: tifulTasksApi.reducer,
  [specialOfferApi.reducerPath]: specialOfferApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [notificationPageApi.reducerPath]: notificationPageApi.reducer,
  [aiConversationsApi.reducerPath]: aiConversationsApi.reducer,
  notificationPage: notificationPageReducer,
  [areasApi.reducerPath]: areasApi.reducer,
  [questionsApi.reducerPath]: questionsApi.reducer,
  [citiesApi.reducerPath]: citiesApi.reducer,
  [statesApi.reducerPath]: statesApi.reducer,
  [inventoryMovementsApi.reducerPath]: inventoryMovementsApi.reducer,
  [settingsApi.reducerPath]: settingsApi.reducer,
  [stockItemsApi.reducerPath]: stockItemsApi.reducer,
  [stockInventoryApi.reducerPath]: stockInventoryApi.reducer,
  [supplierApi.reducerPath]: supplierApi.reducer,
  [warehousesApi.reducerPath]: warehousesApi.reducer,
  [stockOwnersApi.reducerPath]: stockOwnersApi.reducer,
  [tableSettingsApi.reducerPath]: tableSettingsApi.reducer,
  [filterSettingsApi.reducerPath]: filterSettingsApi.reducer,
  [searchApi.reducerPath]: searchApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [adminsApi.reducerPath]: adminsApi.reducer,
  [publicApi.reducerPath]: publicApi.reducer,
  [shippingApi.reducerPath]: shippingApi.reducer,
  [attachmentsApi.reducerPath]: attachmentsApi.reducer,
  [invitationsApi.reducerPath]: invitationsApi.reducer,
  [companyIntegrationsApi.reducerPath]: companyIntegrationsApi.reducer,
  [companiesApi.reducerPath]: companiesApi.reducer,
  [receiptsApi.reducerPath]: receiptsApi.reducer,
});

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['user'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const setupStore = () => {
  return configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE', 'persist/REGISTER'],
        },
      }).concat(
        userApi.middleware,
        rtkQueryErrorLogger,
        notificationMiddleware,
        driversApi.middleware,
        dashboardApi.middleware,
        agentsApi.middleware,
        productsApi.middleware,
        palletesApi.middleware,
        itemsApi.middleware,
        categoriesApi.middleware,
        attributesApi.middleware,
        linesApi.middleware,
        vehiclesApi.middleware,
        clientsApi.middleware,
        customersApi.middleware,
        ordersApi.middleware,
        notificationsApi.middleware,
        notificationPageApi.middleware,
        aiConversationsApi.middleware,
        citiesApi.middleware,
        statesApi.middleware,
        inventoryMovementsApi.middleware,
        settingsApi.middleware,
        stockItemsApi.middleware,
        stockInventoryApi.middleware,
        supplierApi.middleware,
        warehousesApi.middleware,
        stockOwnersApi.middleware,
        tableSettingsApi.middleware,
        filterSettingsApi.middleware,
        searchApi.middleware,
        authApi.middleware,
        adminsApi.middleware,
        publicApi.middleware,
        shippingApi.middleware,
        attachmentsApi.middleware,
        invitationsApi.middleware,
        companyIntegrationsApi.middleware,
        companiesApi.middleware,
        receiptsApi.middleware,
      ) as any,
  });
};

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
export const store = setupStore();
export const persistor = persistStore(store);
